/**
 * When the contacts were last synced, whether a sync is running now, and the way to ask for another.
 *
 * A running sync is shown as running: a spinner, how many contacts it has read so far, and a note
 * that it carries on in the background. It used to be one grey sentence beside a disabled button,
 * which read as finished or broken.
 *
 * A sync needs the advisor's agreement, so the button is off while that agreement is known to be
 * missing, with a sentence saying where to give it. While the answer is unknown the button stays on
 * and the server decides. Whatever the server refuses is shown here, never swallowed.
 */
import { useId } from 'react';
import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@design-system';
import type { useCrmBookSync } from '@client/hooks/useCrmBookSync';
import { fieldMapCopy } from '../lib/field-map-copy';
import { FieldMapAlert } from './field-map-alert';

const REFRESH_CHOICES = [
  { hours: 1, label: 'Every hour' },
  { hours: 6, label: 'Every 6 hours' },
  { hours: 12, label: 'Twice a day' },
  { hours: 24, label: 'Daily' },
  { hours: 24 * 7, label: 'Weekly' },
  { hours: 24 * 30, label: 'Monthly' },
];

interface BookSyncStatusProps {
  crmName: string;
  sync: ReturnType<typeof useCrmBookSync>;
  /** True only when the advisor is known not to have agreed to the full contact read. */
  consentMissing: boolean;
}

interface SyncStateProps {
  crmName: string;
  sync: ReturnType<typeof useCrmBookSync>;
}

function SyncState({ crmName, sync }: SyncStateProps) {
  const copy = fieldMapCopy.sync;
  const finishedAt = sync.lastCompleted?.finishedAt;

  if (sync.statusUnavailable) {
    return (
      <p className="flex items-start gap-2 text-gray-700">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        {copy.unavailable(crmName)}
      </p>
    );
  }
  if (sync.active) {
    return (
      <div className="flex items-start gap-2">
        <Spinner
          aria-hidden="true"
          className="text-primary mt-0.5 shrink-0"
        />
        <div>
          <p className="font-medium text-gray-900">{copy.running(crmName)}</p>
          <p className="text-gray-600">
            {copy.progress(sync.active.recordsSeen ?? 0)} {copy.background}
          </p>
        </div>
      </div>
    );
  }
  if (finishedAt) {
    return (
      <div className="flex items-start gap-2">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-700" />
        <div>
          <p className="text-gray-700">{copy.last(crmName, new Date(finishedAt).toLocaleString())}</p>
          {sync.lastCompletedWasPartial && <p className="text-amber-700">{copy.partial}</p>}
        </div>
      </div>
    );
  }
  if (sync.lastFailure) {
    return (
      <p className="flex items-start gap-2 text-red-700">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        {copy.failed(crmName)}
      </p>
    );
  }
  return (
    <p className="flex items-start gap-2 text-gray-700">
      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
      {copy.never(crmName)}
    </p>
  );
}

export function BookSyncStatus({ crmName, sync, consentMissing }: BookSyncStatusProps) {
  const intervalId = useId();
  const copy = fieldMapCopy.sync;

  if (sync.isLoading) return null;

  return (
    <section className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div role="status">
          <SyncState
            crmName={crmName}
            sync={sync}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-gray-700">
            <Label
              htmlFor={intervalId}
              className="font-normal whitespace-nowrap"
            >
              {copy.schedule}
            </Label>
            <Select
              value={String(sync.refreshIntervalHours ?? sync.settings?.refreshIntervalHours ?? 24)}
              onValueChange={value => sync.setRefreshInterval(Number(value))}
              disabled={sync.isSavingSettings}
            >
              <SelectTrigger
                id={intervalId}
                className="w-40 bg-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REFRESH_CHOICES.map(choice => (
                  <SelectItem
                    key={choice.hours}
                    value={String(choice.hours)}
                  >
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            onClick={sync.requestSync}
            disabled={sync.isRequesting || !!sync.active || consentMissing}
          >
            {sync.isRequesting ? (
              <>
                <Spinner aria-hidden="true" />
                {copy.starting}
              </>
            ) : sync.active ? (
              <>
                <Spinner aria-hidden="true" />
                {copy.syncing}
              </>
            ) : (
              copy.syncNow
            )}
          </Button>
        </div>
      </div>

      {consentMissing ? (
        <p className="mt-3 text-gray-700">{copy.locked}</p>
      ) : (
        !!sync.requestError && (
          <FieldMapAlert
            title={fieldMapCopy.alerts.syncTitle}
            error={sync.requestError}
            className="mt-3"
          />
        )
      )}
      {!!sync.settingsError && (
        <FieldMapAlert
          title={fieldMapCopy.alerts.settingsTitle}
          error={sync.settingsError}
          className="mt-3"
        />
      )}
    </section>
  );
}
